import React, { useEffect, useMemo, useState } from 'react'
import { compressImage } from '../utils/imageCompression'
import { useNavigate } from 'react-router-dom'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'
import type { Department, Program } from '../types'
import { SkillsPicker } from '../components/SkillsPicker'
import {
  EMPLOYMENT_STATUS_OPTIONS, EMPLOYER_TYPE_OPTIONS, JOB_RELEVANCE_OPTIONS,
  SALARY_RANGE_OPTIONS, EMPLOYMENT_LENGTH_OPTIONS, FURTHER_STUDIES_OPTIONS,
  isEmployed, toDbValue, toDisplayValue,
} from '../constants/profileOptions'
import { 
  UserCircleIcon, 
  BriefcaseIcon, 
  AcademicCapIcon,
  PencilSquareIcon,
  CameraIcon
} from '@heroicons/react/24/outline'
import { CareerTimeline } from '../components/CareerTimeline'

interface AlumniProfileData {
  first_name?: string
  last_name?: string
  age?: number
  gender?: string
  phone?: string
  program?: string
  department?: string
  year_graduated?: number
  graduation_year?: number
  current_employment_status?: string
  field_of_work?: string
  job_position?: string
  employer_type?: string
  job_relevance?: string
  salary_range?: string
  employment_length?: string
  unemployment_reasons?: string
  skills_acquired?: string[]
  further_studies?: string[]
  career_progression?: string[]
  job_search_methods?: string[]
  profile_picture?: string | null
}

const AlumniProfilePage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<AlumniProfileData | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editedProfile, setEditedProfile] = useState<AlumniProfileData>({})
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [departments, setDepartments] = useState<Department[]>([])
  const [allPrograms, setAllPrograms] = useState<Program[]>([])
  const graduationYears = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: currentYear - 1900 + 1 }, (_, index) => currentYear - index)
  }, [])

  // Whether the current employment status means job-related fields are relevant
  const employed = isEmployed(editedProfile.current_employment_status)

  // Fetch departments and programs from database
  const fetchDepartmentsAndPrograms = async () => {
    try {
      // Fetch departments
      const { data: depts, error: deptsError } = await supabaseClient
        .from('departments')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (deptsError) throw deptsError

      // Fetch programs
      const { data: progs, error: progsError } = await supabaseClient
        .from('programs')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (progsError) throw progsError

      setDepartments(depts || [])
      setAllPrograms(progs || [])
    } catch (err: any) {
      console.error('Error fetching departments and programs:', err)
      toast.error('Failed to load departments and programs')
    }
  }

  // Get available programs based on selected department
  const getProgramsByDepartment = (departmentName: string): Program[] => {
    const dept = departments.find(d => d.name === departmentName)
    if (!dept) return []
    return allPrograms.filter(p => p.department_id === dept.id)
  }

  // Normalize program value: convert full name to code if needed
  const normalizeProgramValue = (programValue: string): string => {
    if (!programValue) return ''
    
    // Check if it's already a code (short format like "BSCS")
    const programByCode = allPrograms.find(p => p.code === programValue)
    if (programByCode) return programValue
    
    // Try to find by full name and return the code
    const programByName = allPrograms.find(p => p.full_name === programValue || p.name === programValue)
    if (programByName) return programByName.code || programByName.name
    
    // If not found, return as-is
    return programValue
  }

  useEffect(() => {
    if (!user) return
    if (user.role === 'alumni') {
      supabaseClient
        .from('alumni_profiles')
        .select('approval_status')
        .eq('user_id', user.id)
        .single()
        .then(({ data }) => {
          if (data && data.approval_status !== 'approved') {
            navigate('/pending-approval')
          }
        })
    }
    fetchProfile()
    fetchDepartmentsAndPrograms()
  }, [user])

  // Re-normalize program value when allPrograms is loaded
  useEffect(() => {
    if (allPrograms.length > 0 && editedProfile.program) {
      const normalizedProgram = normalizeProgramValue(editedProfile.program)
      if (normalizedProgram !== editedProfile.program) {
        setEditedProfile(prev => ({ ...prev, program: normalizedProgram }))
      }
    }
  }, [allPrograms])

  const fetchProfile = async () => {
    if (!user) return

    try {
      setLoading(true)
      const { data, error } = await supabaseClient
        .from('alumni_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
        if (error.code !== 'PGRST116') { // Not found error
          toast.error('Unable to fetch profile')
        }
        return
      }

      setProfile(data)
      // Convert database enum values to display values
      const displayData = {
        ...data,
        program: normalizeProgramValue(data.program || ''),
        current_employment_status: toDisplayValue(data.current_employment_status || '', 'employment_status'),
        employer_type: toDisplayValue(data.employer_type || '', 'employer_type'),
        job_relevance: toDisplayValue(data.job_relevance || '', 'job_relevance'),
        salary_range: toDisplayValue(data.salary_range || '', 'salary_range'),
        employment_length: toDisplayValue(data.employment_length || '', 'employment_length')
      }
      setEditedProfile(displayData)
      setImagePreview(data.profile_picture || '')
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB')
        return
      }
      setSelectedFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadProfilePicture = async (): Promise<string | null> => {
    if (!selectedFile || !user) return null

    try {
      const compressed = await compressImage(selectedFile, { maxWidth: 400, maxHeight: 400, quality: 0.85 })
      const fileName = `${user.id}-${Date.now()}.jpg`
      const filePath = `alumni-profiles/${fileName}`

      const { error: uploadError } = await supabaseClient.storage
        .from('alumni-profiles')
        .upload(filePath, compressed, {
          upsert: true,
          contentType: 'image/jpeg'
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        if (uploadError.message.includes('not found') || uploadError.message.includes('bucket')) {
          toast.error('Storage bucket not set up. Please contact administrator.')
        } else if (uploadError.message.includes('policy')) {
          toast.error('Permission denied. Please contact administrator.')
        } else {
          toast.error(`Upload failed: ${uploadError.message}`)
        }
        throw uploadError
      }

      const { data } = supabaseClient.storage
        .from('alumni-profiles')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error: any) {
      console.error('Error uploading image:', error)
      return null
    }
  }

  const handleSave = async () => {
    if (!user) return

    const trimmedFirstName = (editedProfile.first_name || '').trim()
    if (!trimmedFirstName) {
      toast.error('First name is required')
      return
    }

    const trimmedLastName = (editedProfile.last_name || '').trim()
    if (!trimmedLastName) {
      toast.error('Last name is required')
      return
    }

    const departmentValue = (editedProfile.department || '').trim()
    if (!departmentValue) {
      toast.error('College/Department is required')
      return
    }

    const programValue = (editedProfile.program || '').trim()
    if (!programValue) {
      toast.error('Program is required')
      return
    }

    const graduationYearValue = editedProfile.graduation_year ?? editedProfile.year_graduated
    const parsedGraduationYear = typeof graduationYearValue === 'number'
      ? graduationYearValue
      : parseInt((graduationYearValue as unknown as string) || '', 10)

    if (!parsedGraduationYear || Number.isNaN(parsedGraduationYear)) {
      toast.error('Graduation year is required')
      return
    }

    const currentYear = new Date().getFullYear()
    if (parsedGraduationYear < 1900 || parsedGraduationYear > currentYear) {
      toast.error(`Graduation year must be between 1900 and ${currentYear}`)
      return
    }

    const normalizedProfile: AlumniProfileData = {
      ...editedProfile,
      first_name: trimmedFirstName,
      last_name: trimmedLastName,
      department: departmentValue,
      program: programValue,
      year_graduated: parsedGraduationYear
    }

    // Remove graduation_year as it doesn't exist in the database
    const { graduation_year, ...profileToUpdate } = normalizedProfile

    try {
      setSaving(true)

      const programChanged = profile?.program !== normalizedProfile.program

      let profilePictureUrl = normalizedProfile.profile_picture
      if (selectedFile) {
        const uploadedUrl = await uploadProfilePicture()
        if (uploadedUrl) {
          profilePictureUrl = uploadedUrl
        } else {
          toast.error('Failed to upload profile picture')
          setSaving(false)
          return
        }
      }

      const { error } = await supabaseClient
        .from('alumni_profiles')
        .update({
          ...profileToUpdate,
          current_employment_status: toDbValue(normalizedProfile.current_employment_status || '', 'employment_status'),
          employer_type: toDbValue(normalizedProfile.employer_type || '', 'employer_type'),
          job_relevance: toDbValue(normalizedProfile.job_relevance || '', 'job_relevance'),
          salary_range: toDbValue(normalizedProfile.salary_range || '', 'salary_range'),
          employment_length: toDbValue(normalizedProfile.employment_length || '', 'employment_length'),
          profile_picture: profilePictureUrl,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)

      if (error) {
        console.error('Error updating profile:', error)
        toast.error('Failed to update profile')
        return
      }

      toast.success('Profile updated successfully!')
      if (programChanged) {
        toast.success('Your program has been updated. Available surveys may have changed.', {
          duration: 4000
        })
      }

      const updatedProfile = { ...normalizedProfile, profile_picture: profilePictureUrl }
      setProfile(updatedProfile)
      setEditedProfile(updatedProfile)
      setIsEditing(false)
      setSelectedFile(null)
    } catch (error) {
      console.error('Error:', error)
      toast.error('An error occurred while updating profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    // Reset to original profile data with display values
    const displayData = profile ? {
      ...profile,
      current_employment_status: toDisplayValue(profile.current_employment_status || '', 'employment_status'),
      employer_type: toDisplayValue(profile.employer_type || '', 'employer_type'),
      job_relevance: toDisplayValue(profile.job_relevance || '', 'job_relevance'),
      salary_range: toDisplayValue(profile.salary_range || '', 'salary_range'),
      employment_length: toDisplayValue(profile.employment_length || '', 'employment_length')
    } : {}
    setEditedProfile(displayData)
    setImagePreview(profile?.profile_picture || '')
    setSelectedFile(null)
    setIsEditing(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Alumni Profile</h1>
          <p className="mt-2 text-gray-600">
            Manage your professional profile and preferences
          </p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PencilSquareIcon className="h-5 w-5" />
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 rounded-lg transition-colors ${
                saving
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Profile Picture Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <CameraIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Profile Picture</h2>
        </div>
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
              {imagePreview || editedProfile.profile_picture ? (
                <img
                  src={imagePreview || editedProfile.profile_picture || ''}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/default-avatar.svg'
                  }}
                />
              ) : (
                <UserCircleIcon className="w-20 h-20 text-gray-400" />
              )}
            </div>
            {isEditing && (
              <label
                htmlFor="profile-picture-upload"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
              >
                <CameraIcon className="h-5 w-5" />
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {editedProfile.first_name && editedProfile.last_name
                ? `${editedProfile.first_name} ${editedProfile.last_name}`
                : user?.email}
            </h3>
            <p className="text-sm text-gray-500 mb-1">
              {user?.email}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              {editedProfile.program || 'Program not set'}
            </p>
            {user?.created_at && (
              <p className="text-xs text-gray-500 mb-2">
                Joined on {new Date(user.created_at).toLocaleDateString('en-PH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}
            {isEditing && (
              <p className="text-xs text-gray-500">
                Click the camera icon to upload a new profile picture (max 5MB)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <UserCircleIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            <input
              type="text"
              value={editedProfile.first_name || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, first_name: e.target.value })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Enter first name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <input
              type="text"
              value={editedProfile.last_name || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, last_name: e.target.value })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Enter last name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
            <input
              type="number"
              value={editedProfile.age || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, age: parseInt(e.target.value) || undefined })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Enter age"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <select
              value={editedProfile.gender || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, gender: e.target.value })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            <div className={`flex rounded-lg border overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 ${
              isEditing ? 'border-gray-300 bg-white' : 'border-gray-300 bg-gray-50'
            }`}>
              <PhoneInput
                international
                defaultCountry="PH"
                value={editedProfile.phone || undefined}
                onChange={(val) => setEditedProfile({ ...editedProfile, phone: val || '' })}
                disabled={!isEditing}
                className="phone-input-custom w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Academic Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <AcademicCapIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Academic Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <select
              value={editedProfile.department || ''}
              onChange={(e) => {
                const newDepartment = e.target.value
                setEditedProfile({ ...editedProfile, department: newDepartment, program: '' })
              }}
              disabled={true}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            >
              <option value="">{!editedProfile.department ? 'Not specified' : 'Select Department'}</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.name}>
                  {dept.name}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">This field cannot be changed after graduation</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Program</label>
            <select
              value={editedProfile.program || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, program: e.target.value })}
              disabled={true}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            >
              <option value="">{!editedProfile.program ? 'Not specified' : 'Select Program'}</option>
              {getProgramsByDepartment(editedProfile.department || '').map((program) => (
                <option key={program.id} value={program.code || program.name}>
                  {program.full_name || program.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">This field cannot be changed after graduation</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year Graduated</label>
            <select
              value={editedProfile.graduation_year?.toString() || editedProfile.year_graduated?.toString() || ''}
              onChange={(e) => {
                const year = e.target.value ? parseInt(e.target.value, 10) : undefined
                setEditedProfile({
                  ...editedProfile,
                  graduation_year: year,
                  year_graduated: year
                })
              }}
              disabled={true}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            >
              <option value="">{!editedProfile.graduation_year && !editedProfile.year_graduated ? 'Not specified' : 'Select Year'}</option>
              {graduationYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">This field cannot be changed after graduation</p>
          </div>
        </div>
      </div>

      {/* Employment Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <BriefcaseIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Employment Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status</label>
            <select
              value={editedProfile.current_employment_status || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, current_employment_status: e.target.value })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            >
              <option value="">Select status</option>
              {EMPLOYMENT_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Job-related fields: only shown when employed/self-employed */}
          {employed && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Position</label>
                <input
                  type="text"
                  value={editedProfile.job_position || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, job_position: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="Enter job position"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field of Work</label>
                <input
                  type="text"
                  value={editedProfile.field_of_work || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, field_of_work: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="e.g., Software Development"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employer Type</label>
                <select
                  value={editedProfile.employer_type || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, employer_type: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                >
                  <option value="">Select type</option>
                  {EMPLOYER_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Salary Range</label>
                <select
                  value={editedProfile.salary_range || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, salary_range: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                >
                  <option value="">Select range</option>
                  {SALARY_RANGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Relevance</label>
                <select
                  value={editedProfile.job_relevance || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, job_relevance: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                >
                  <option value="">Select relevance</option>
                  {JOB_RELEVANCE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Length</label>
                <select
                  value={editedProfile.employment_length || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, employment_length: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                >
                  <option value="">Select duration</option>
                  {EMPLOYMENT_LENGTH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </>
          )}

          {/* Unemployment reasons: only shown when status is Unemployed */}
          {editedProfile.current_employment_status === 'Unemployed' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unemployment Reasons</label>
              <textarea
                value={editedProfile.unemployment_reasons || ''}
                onChange={(e) => setEditedProfile({ ...editedProfile, unemployment_reasons: e.target.value })}
                disabled={!isEditing}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                placeholder="Please share your reasons or current situation"
                rows={2}
              />
            </div>
          )}
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <AcademicCapIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Additional Information</h2>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <SkillsPicker
              label="Skills Acquired"
              value={editedProfile.skills_acquired || []}
              onChange={(skills) => setEditedProfile({ ...editedProfile, skills_acquired: skills })}
              disabled={!isEditing}
              placeholder="Search skills or type a custom one…"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Further Studies</label>
            <div className="space-y-2 p-3 border border-gray-300 rounded-lg bg-gray-50">
              {FURTHER_STUDIES_OPTIONS.map((option) => (
                <label key={option} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="further_studies"
                    checked={editedProfile.further_studies?.[0] === option}
                    onChange={() => {
                      setEditedProfile({ 
                        ...editedProfile, 
                        further_studies: [option]
                      })
                    }}
                    disabled={!isEditing}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className={isEditing ? 'text-gray-700' : 'text-gray-600'}>{option}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Career Progression</label>
            <textarea
              value={editedProfile.career_progression?.join(', ') || ''}
              onChange={(e) => setEditedProfile({ 
                ...editedProfile, 
                career_progression: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : [] 
              })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Describe your career progression since graduation"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Job Search Methods</label>
            <textarea
              value={editedProfile.job_search_methods?.join(', ') || ''}
              onChange={(e) => setEditedProfile({ 
                ...editedProfile, 
                job_search_methods: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : [] 
              })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Enter methods separated by commas (e.g., Job Portals, Networking, Direct Application)"
              rows={2}
            />
          </div>
        </div>
      </div>
      <CareerTimeline alumniId={user!.id} canEdit={true} />
    </div>
  )
}

export default AlumniProfilePage